import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { config } from "../../../config";
import { getCaptchaToken } from "../../../forms/Captcha";
import { guardUnhandledAction } from "../../../utils/never";
import { IReduxState } from "../reducers.interfaces";
import {
    getBillingAddress,
    getShippingAddress,
    serializeCheckoutError,
} from "../utils";
import { completeOrderPayment } from "./completeOrderPayment";

/**
 * Build payment method data for order placement API.
 *
 * This function converts the Redux payment method state into the
 * API format expected by the checkout endpoint. It handles all
 * payment types including cash, microform, bluefin, and financing.
 *
 * @param {IReduxState} state - Current Redux state
 * @returns {Object} Payment methods object for API
 *
 * @example
 * const payment = getOrderPayment(state);
 * // Returns: { primary: { method_type: "cybersource-microform", enabled: true, ... } }
 */
export const getOrderPayment = (state: IReduxState) => {
    type IBasePaymentMethod = {
        enabled: boolean;
        amount?: string;
        pay_balance?: boolean;
    };
    type ICash = IBasePaymentMethod & {
        method_type: "cash";
    };
    type IPayLater = IBasePaymentMethod & {
        method_type: "pay-later";
    };
    type ICybersourceMicroform = IBasePaymentMethod & {
        method_type: "cybersource-microform";
        transient_token: string;
    };
    type IBluefin = IBasePaymentMethod & {
        method_type: "bluefin";
        payment_data: string;
    };
    type IFinancing = IBasePaymentMethod & {
        method_type: "financing";
        account_number: string;
        financing_plan: number | null;
    };
    type IAPIPaymentMethod =
        | ICash
        | IPayLater
        | ICybersourceMicroform
        | IBluefin
        | IFinancing;
    type IAPIPaymentMethods = { [method_key: string]: IAPIPaymentMethod };
    const methods: IAPIPaymentMethods = {};
    const methodState = state.form.payment_methods;
    Object.keys(methodState).forEach((methodKey) => {
        const data = methodState[methodKey];
        let method: IAPIPaymentMethod;
        switch (data.method_type) {
            case "default":
                throw new Error("Method type should never be default.");

            case "cash":
                method = {
                    method_type: "cash",
                    enabled: true,
                };
                break;

            case "pay-later":
                method = {
                    method_type: "pay-later",
                    enabled: true,
                };
                break;

            case "cybersource-microform":
                method = {
                    method_type: "cybersource-microform",
                    enabled: true,
                    transient_token: data.transient_token,
                };
                break;

            case "bluefin":
                method = {
                    method_type: "bluefin",
                    enabled: true,
                    payment_data: data.payment_data,
                };
                break;

            case "financing":
                method = {
                    method_type: "financing",
                    enabled: true,
                    account_number: data.financing_account,
                    financing_plan: data.financing_plan,
                };
                break;

            default:
                guardUnhandledAction(data);
                return;
        }
        if (data.pay_balance) {
            method.pay_balance = true;
        } else {
            method.pay_balance = false;
            method.amount = data.amount || "0.00";
        }
        methods[methodKey] = method;
    });
    return methods;
};

/**
 * Place an order.
 *
 * This thunk creates a new order by:
 * 1. Submitting order with basket, addresses, shipping, and payment info
 * 2. Delegating payment state checking to completeOrderPayment
 *
 * @throws {ICheckoutHTTPError} If order placement fails or payment is declined
 *
 * @sideEffects
 * - Reads basket, addresses, shipping, and payment from state
 * - Generates captcha token if enabled via getCaptchaToken
 * - Makes API call to place order via checkoutAPI.placeOrder
 * - Updates `data.order` with order response via updateOrder action
 * - Delegates payment completion to completeOrderPayment thunk
 */
export const placeOrder = createAsyncThunk(
    "checkout/placeOrder",
    async (_: void, { getState, dispatch }): Promise<void> => {
        const state = getState() as { checkout: IReduxState };
        const checkoutState = state.checkout;

        if (!checkoutState.data.basket) {
            throw new Error("Can not checkout without a basket");
        }

        const basket = checkoutState.data.basket.url;
        const shippingAddr = getShippingAddress(checkoutState);
        const shippingMethod = checkoutState.form.shipping_method;
        const billingAddr = getBillingAddress(checkoutState);
        const guestEmail = checkoutState.form.guest_email;
        const payment = getOrderPayment(checkoutState);
        const recaptcha = config.get("ENABLE_CHECKOUT_CAPTCHA")
            ? await getCaptchaToken("checkout")
            : null;

        const order = await checkoutAPI.placeOrder(
            basket,
            shippingAddr,
            shippingMethod,
            billingAddr,
            payment,
            guestEmail,
            recaptcha || undefined,
        );

        // Import actions dynamically to avoid circular dependency
        const { updateOrder } = await import("../reducers");
        dispatch(updateOrder(order));

        await dispatch(completeOrderPayment()).unwrap();
    },
    { serializeError: serializeCheckoutError },
);
